

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useState, useEffect } from 'react';
import { $getRoot, TextNode } from 'lexical';

import type { Entity, EntityAction } from '../../types/entity';
import { EntityContextMenu } from './EntityContextMenu';



interface EntityHighlightPluginProps {
  entities?: Entity[];
}


// Utility: Map entity ranges to node-aligned segments
function getNodeAlignedEntitySegments(entities: Entity[], root: ReturnType<typeof $getRoot>) {
  const segments: Array<{
    nodeKey: string;
    start: number;
    end: number;
    entity: Entity;
  }> = [];
  let globalOffset = 0;

  root.getChildren().forEach((node) => {
    if (node.getType() === 'text') {
      const textNode = node as TextNode;
      const nodeText = textNode.getTextContent();
      const nodeSize = nodeText.length;

      entities.forEach((entity) => {
        const overlapStart = Math.max(entity.start, globalOffset);
        const overlapEnd = Math.min(entity.end, globalOffset + nodeSize);
        if (overlapStart < overlapEnd) {
          segments.push({
            nodeKey: textNode.getKey(),
            start: overlapStart - globalOffset,
            end: overlapEnd - globalOffset,
            entity,
          });
        }
      });

      globalOffset += nodeSize;
    }
  });

  return segments;
}




export function EntityHighlightPlugin({ entities }: EntityHighlightPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [contextMenu, setContextMenu] = useState<{
    entity: Entity;
    actions: EntityAction[];
    position: { x: number; y: number };
  } | null>(null);


  // Register decorators using Lexical's supported API
  useEffect(() => {
    if (!entities || entities.length === 0) return;
    return editor.registerDecoratorListener((activeEditor) => {
      // Type-safe cast for LexicalEditor
      const editorInstance = activeEditor as unknown as import('lexical').LexicalEditor;
      if (typeof editorInstance.getEditorState !== 'function') return {};
      return editorInstance.getEditorState().read(() => {
        const root = $getRoot();
        const segments = getNodeAlignedEntitySegments(entities, root);
        const decoratorsByNode: Record<string, Array<{ start: number; end: number; decorator: React.ReactNode }>> = {};
        segments.forEach(({ nodeKey, start, end, entity }) => {
          if (!decoratorsByNode[nodeKey]) decoratorsByNode[nodeKey] = [];
          decoratorsByNode[nodeKey].push({
            start,
            end,
            decorator: (
              <span
                className="entity-highlight"
                title={`${entity.type}: ${entity.value}`}
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = (e.target as HTMLElement).getBoundingClientRect();
                  const pos = { x: rect.left + window.scrollX, y: rect.bottom + window.scrollY };
                  setContextMenu({ entity, actions: entity.actions || [], position: pos });
                }}
                onMouseEnter={e => {
                  (e.target as HTMLElement).style.background = '#fff3cd';
                }}
                onMouseLeave={e => {
                  (e.target as HTMLElement).style.background = '#ffeeba';
                }}
                style={{
                  background: '#ffeeba',
                  borderBottom: '2px dotted #bfa500',
                  cursor: 'pointer',
                  transition: 'background 0.2s, box-shadow 0.2s',
                  outline: 'none',
                }}
              />
            ),
          });
        });
        return decoratorsByNode;
      });
    });
  }, [editor, entities]);

  // Handle action selection
  const handleSelect = (action: EntityAction, entity: Entity) => {
    setContextMenu(null);
    window.dispatchEvent(new CustomEvent('entityAction', { detail: { entity, action } }));
  };

  return (
    <>
      {contextMenu && (
        <EntityContextMenu
          entity={contextMenu.entity}
          actions={contextMenu.actions}
          position={contextMenu.position}
          onSelect={handleSelect}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  );
}

// Add some basic CSS for .entity-highlight (can be moved to a CSS file)
if (typeof window !== 'undefined' && document) {
  const style = document.createElement('style');
  style.innerHTML = `
    .entity-highlight {
      background: #ffeeba;
      border-bottom: 2px dotted #bfa500;
      cursor: pointer;
      transition: background 0.2s, box-shadow 0.2s;
      outline: none;
    }
    .entity-highlight:focus {
      box-shadow: 0 0 0 2px #0078d4;
    }
    .entity-highlight:hover {
      background: #fff3cd;
    }
  `;
  document.head.appendChild(style);
}
